import { Grid, Column, Button, InlineNotification } from '@carbon/react'
import { Renew } from '@carbon/icons-react'
import { PeriodSelector } from '../shared'
import { PersonalMetricsCards } from './PersonalMetricsCards'
import { PersonalEngagementChart } from './PersonalEngagementChart'
import { PersonalTopActivities } from './PersonalTopActivities'
import { PersonalRecentAttempts } from './PersonalRecentAttempts'
import { PersonalInsights } from './PersonalInsights'
import styles from './PersonalAnalytics.module.scss'

export interface PersonalAnalyticsDashboardProps {
  totalActivities: number
  totalAttempts: number
  averageScore: number | null
  completionRate: number
  insights: Array<{
    type: string
    title: string
    description: string
    trend?: 'positive' | 'negative' | 'neutral'
    value?: unknown
  }>
  topActivities: Array<{
    activityId: string
    avgScore: number
    totalAttempts: number
    successRate: number
  }>
  recentAttempts: Array<{
    event_id: string
    occurred_at: string
    activity_id: string
    activity_kind_code: string
    activity_title?: string
    duration_ms: number
    score_percent?: number
    pass: boolean
  }>
  dailyAttempts: Array<{ date: string; attempts: number }>
  selectedPeriod: string
  onPeriodChange: (period: string) => void
  onRefresh: () => void
  health?: string | null
  error?: string | null
  isLoading?: boolean
}

export function PersonalAnalyticsDashboard({
  totalActivities,
  totalAttempts,
  averageScore,
  completionRate,
  insights,
  topActivities,
  recentAttempts,
  dailyAttempts,
  selectedPeriod,
  onPeriodChange,
  onRefresh,
  error,
}: PersonalAnalyticsDashboardProps) {
  return (
    <div className={styles.dashboardContainer}>
      <Grid narrow>
        {/* Header */}
        <Column lg={16} md={8} sm={4}>
          <div className={styles.header}>
            <div className={styles.headerContent}>
              <h1 className={styles.pageTitle}>Mes Activités</h1>
              <p className={styles.pageDescription}>
                Performances et progression sur la période sélectionnée
              </p>
              {error && (
                <InlineNotification
                  kind="error"
                  title="Erreur"
                  subtitle={error}
                  lowContrast
                  className={styles.errorNotification}
                />
              )}
            </div>
            <div className={styles.headerActions}>
              <PeriodSelector
                value={selectedPeriod}
                onChange={onPeriodChange}
                id="period-select"
                size="sm"
                className={styles.periodSelect}
              />
              <Button renderIcon={Renew} kind="tertiary" onClick={onRefresh}>
                Actualiser
              </Button>
            </div>
          </div>
        </Column>

        {/* KPI Cards */}
        <Column lg={16} md={8} sm={4}>
          <div className={styles.metricsGrid}>
            <PersonalMetricsCards
              totalActivities={totalActivities}
              totalAttempts={totalAttempts}
              averageScore={averageScore}
              completionRate={completionRate}
            />
          </div>
        </Column>

        {/* Engagement Chart */}
        <Column lg={16} md={8} sm={4}>
          <div className={styles.section}>
            <PersonalEngagementChart dailyAttempts={dailyAttempts} />
          </div>
        </Column>

        {/* Top Activities + Insights */}
        <Column lg={8} md={4} sm={4}>
          <div className={styles.section}>
            <PersonalTopActivities activities={topActivities} />
          </div>
        </Column>
        <Column lg={8} md={4} sm={4}>
          <div className={styles.section}>
            <PersonalInsights insights={insights} />
          </div>
        </Column>

        {/* Recent Attempts */}
        <Column lg={16} md={8} sm={4}>
          <div className={styles.section}>
            <PersonalRecentAttempts attempts={recentAttempts} />
          </div>
        </Column>
      </Grid>
    </div>
  )
}
